import React, { memo, useCallback, useContext } from 'react'

import {
  Button,
  Icon,
  SearchGradient,
  ImageAIGradient,
  BrushAIGradient,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useRequireLogin from 'Pods/ThreeDStudio/Hooks/useRequireLogin'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { openImageAssistant, openImageEditor } from 'Pods/ThreeDStudio/Store/actions/ui'
import { IMAGE_ASSISTANT_TAB } from 'Pods/ThreeDStudio/Store/constants'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

const searchIcon = <Icon svgComponent={SearchGradient} size={iconSize} notSpaced />
const createIcon = <Icon svgComponent={ImageAIGradient} size={iconSize} notSpaced />
const editIcon = <Icon svgComponent={BrushAIGradient} size={iconSize} notSpaced />

function IdeationActions() {
  const t = useTranslation()
  const { imageSearchOnIdeationFLowEnabled, userLoggedIn } = useContext(ThreeDStudioPageContext)
  const requireLogin = useRequireLogin()
  const inputImage = useThreeDStudioStore(state => state.modelSettings.inputImage)
  const inputImageUrl = useThreeDStudioStore(state => state.modelSettings.inputImageUrl)
  const openedModelId = useThreeDStudioStore(state => state.ui?.openedModel?.id)
  const hasInputImage = !!inputImage
  // An opened generation shows a past run's inputs, so creating a fresh image there has no
  // place - Create belongs to the new-generation flow only - and Edit is offered only when
  // that run actually came with a reference image to edit.
  const isOpenedGeneration = openedModelId != null
  // A signed-out visitor can never have an input image (uploading prompts registration first),
  // so Edit stays clickable for them and prompts registration instead of sitting greyed out.
  const canEdit = hasInputImage || !userLoggedIn
  const showSearch = !!imageSearchOnIdeationFLowEnabled
  const showCreate = !isOpenedGeneration
  const showEdit = isOpenedGeneration ? hasInputImage : true

  const handleSearch = useCallback(() => {
    if (!requireLogin()) return

    openImageAssistant(IMAGE_ASSISTANT_TAB.SEARCH)
  }, [requireLogin])

  const handleCreate = useCallback(() => {
    if (!requireLogin()) return

    openImageAssistant(IMAGE_ASSISTANT_TAB.CREATE)
  }, [requireLogin])

  // Load the current input image (the upload preview) onto the Edit screen's canvas.
  // No return tab: "Exit Edit mode" closes the panel back to these controls.
  const handleEdit = useCallback(() => {
    if (!requireLogin()) return

    openImageEditor({ id: inputImage, url: inputImageUrl })
  }, [requireLogin, inputImage, inputImageUrl])

  if (!showSearch && !showCreate && !showEdit) return null

  return (
    <div className="image-uploader__ideation-actions" data-image-assistant-trigger>
      {showSearch && (
        <Button
          type="secondary"
          size="small"
          variant="with-icon"
          customClass="image-uploader__ideation-button"
          icon={searchIcon}
          onClick={handleSearch}
        >
          <span className="image-uploader__ideation-button-label">
            {t('shared.actionSelector.imageUploader.ideation.search')}
          </span>
        </Button>
      )}
      {showCreate && (
        <Button
          type="secondary"
          size="small"
          variant="with-icon"
          customClass="image-uploader__ideation-button"
          icon={createIcon}
          onClick={handleCreate}
        >
          <span className="image-uploader__ideation-button-label">
            {t('shared.actionSelector.imageUploader.ideation.create')}
          </span>
        </Button>
      )}
      {showEdit && (
        <Button
          type="secondary"
          size="small"
          variant="with-icon"
          customClass="image-uploader__ideation-button"
          icon={editIcon}
          onClick={handleEdit}
          disabled={!canEdit}
        >
          <span className="image-uploader__ideation-button-label">
            {t('shared.actionSelector.imageUploader.ideation.edit')}
          </span>
        </Button>
      )}
    </div>
  )
}

export default memo(IdeationActions)
